import type { AssetRef, LocalizedString, NormalizedPoint, Scene, TourDocument } from "@heroncendre/schema";
import { childGroups, groupPath, mapOwnerGroup, rootGroup } from "@heroncendre/core";
import reglages from "./espaces.json";

/**
 * ============================================================================
 *  espaces.ts — Les espaces de la navigation « carnet », lus dans le document
 * ----------------------------------------------------------------------------
 *  Un ESPACE est ce que la brochure numérote : le domaine (vue d'ensemble), puis
 *  la terrasse, le pigeonnier, le château, le jardin, la grange, les accès. Le
 *  handoff les décrivait comme un modèle à part (`SpaceGroup`) ; ils existent
 *  déjà dans le document, et c'est là qu'ils sont réglés (éditeur, `?edit`) :
 *
 *    espace                → groupe enfant du groupe racine (le domaine)
 *    plan d'un espace      → `Group.map.image` (déposé par `pnpm plans`)
 *    sous-zones            → sous-groupes d'un espace (étages, parties) :
 *                            la liste des scènes s'y range, repliable
 *    niveaux (RDC, étage)  → sous-zones qui portent en plus leur plan
 *    zone sur le domaine   → `Group.mapPosition` (sur la carte du parent)
 *    point d'une scène     → `Scene.mapPosition` (sur la carte qui s'applique
 *                            à son groupe : la sienne, ou celle d'un ancêtre)
 *    noms, vignettes       → titres FR/EN et preview des scènes
 *
 *  Ce que le document ne sait pas dire, et qui n'est que de la PRÉSENTATION, vit
 *  dans `espaces.json` : le numéro de zone de la brochure et sa place sur le
 *  domaine (tant que l'éditeur ne sait pas poser `Group.mapPosition`, qui
 *  l'emporte s'il est présent ; un espace sans numéro n'a pas de pastille — le
 *  domaine lui-même en a une : ses scènes propres sont les vues de drone), les
 *  scènes MASQUÉES (toujours dans la visite — entrée, hotspots, parcours — mais
 *  absentes des listes et du plan ; on y reste situé dans leur espace) — sauf
 *  si l'adresse porte un paramètre qui les DÉMASQUE (`/?pratique` rend les
 *  autres vues de drone à qui prépare un événement), le recadrage d'un
 *  espace qui n'a pas son plan (un zoom sur l'illustration du domaine), le style
 *  du plan, et le NORD de chaque panorama — le cap qui pointe vers le haut du
 *  plan, sans quoi le cône de vision tournerait au hasard.
 *
 *  Coordonnées : en fraction de l'IMAGE D'ORIGINE (0–1), comme les écrit
 *  l'éditeur. `SpacePlan` les recalcule pour le recadrage et l'ajustement.
 * ==========================================================================*/

/** Rectangle en pourcentage de l'image d'origine : [x, y, largeur, hauteur]. */
export type Recadrage = readonly [number, number, number, number];

export type StylePlan = "illustration" | "architecte";

export interface Niveau {
  groupeId: string;
  /** Libellé du sélecteur de niveaux (titre du sous-groupe) ; absent s'il n'y en a qu'un. */
  libelle?: LocalizedString;
  image: AssetRef;
  recadrage?: Recadrage;
  style: StylePlan;
}

export interface SceneEspace {
  scene: Scene;
  /** Rang du niveau qui la montre. */
  niveau: number;
  /** Position sur l'image d'origine du niveau ; absente : la scène n'est pas encore placée. */
  position?: NormalizedPoint;
  /** Sous-zone (sous-groupe direct de l'espace) qui la contient ; absente : à la racine de l'espace. */
  sousZone?: string;
}

/** Une partie d'un espace — un étage, une aile — sous laquelle se rangent ses scènes. */
export interface SousZone {
  id: string;
  titre: LocalizedString;
}

export interface Espace {
  id: string;
  titre: LocalizedString;
  /** Pastille numérotée sur le plan du domaine (le domaine compris : ses vues de drone). */
  zone?: { numero: string; position: NormalizedPoint };
  niveaux: Niveau[];
  /**
   * Où l'on arrive par la pastille de la zone : la scène désignée dans
   * `espaces.json` (`entree`), sinon la première. Le château s'ouvre ainsi sur
   * le salon de l'étage, sans changer l'ordre de lecture de la liste.
   */
  entree?: string;
  /** Les scènes proposées, dans l'ordre de lecture (sans les masquées). */
  scenes: SceneEspace[];
  /** Toutes les scènes de l'espace, masquées comprises : pour dire où l'on est. */
  membres: ReadonlySet<string>;
  /** Ses sous-zones qui ont des scènes, dans l'ordre du document. */
  sousZones: SousZone[];
}

interface ReglageEspace {
  zone?: string;
  /** Place de la pastille sur le plan du domaine, [x, y] en fraction de l'image. */
  position?: readonly number[];
  /** Scène où mène la pastille, si ce n'est pas la première de la zone. */
  entree?: string;
  style?: StylePlan;
  recadrage?: readonly number[];
}

const parEspace = reglages.espaces as Record<string, ReglageEspace>;
const nords = reglages.nord as Record<string, number>;
const masquees = new Set<string>(reglages.masquees);
// Lu une fois, comme les scènes réservées (`App.tsx`) : l'adresse ne change pas
// de paramètres en cours de visite.
if (typeof window !== "undefined") {
  const params = new URLSearchParams(window.location.search);
  for (const [parametre, scenes] of Object.entries(reglages.demasquees as Record<string, readonly string[]>)) {
    if (params.has(parametre)) for (const id of scenes) masquees.delete(id);
  }
}

/** Cap du panorama qui pointe vers le haut du plan (0 tant qu'il n'est pas réglé). */
export const nordDe = (sceneId: string): number => nords[sceneId] ?? 0;

const zoneDe = (groupe: { id: string; mapPosition?: readonly number[] }): Espace["zone"] => {
  const reglage = parEspace[groupe.id];
  const place = groupe.mapPosition ?? reglage?.position;
  return reglage?.zone && place && place.length === 2
    ? { numero: reglage.zone, position: { x: place[0]!, y: place[1]! } }
    : undefined;
};

const enRecadrage = (valeur: readonly number[] | undefined): Recadrage | undefined =>
  valeur && valeur.length === 4 ? [valeur[0]!, valeur[1]!, valeur[2]!, valeur[3]!] : undefined;

/** Le groupe et tous ses descendants. */
function sousArbre(tour: TourDocument, groupeId: string): Set<string> {
  const ids = new Set([groupeId]);
  for (const enfant of childGroups(tour, groupeId)) for (const id of sousArbre(tour, enfant.id)) ids.add(id);
  return ids;
}

/** Ordre de lecture du menu : rang des groupes successifs, puis de la scène. */
function ordreDeLecture(tour: TourDocument, a: Scene, b: Scene): number {
  if (a.groupId !== b.groupId) {
    const rangA = groupPath(tour, a.groupId).map((g) => g.order);
    const rangB = groupPath(tour, b.groupId).map((g) => g.order);
    for (let i = 0; i < Math.max(rangA.length, rangB.length); i++) {
      const gauche = rangA[i] ?? -1;
      const droite = rangB[i] ?? -1;
      if (gauche !== droite) return gauche - droite;
    }
  }
  return a.order - b.order;
}

/**
 * Les espaces, le domaine en premier. Un espace sans aucun plan applicable
 * (ni le sien, ni celui du domaine) en garde la liste, sans plan.
 */
export function construireEspaces(tour: TourDocument): Espace[] {
  const racine = rootGroup(tour);
  if (!racine) return [];
  const planRacine = racine.map?.image;

  const espaces: Espace[] = [];
  const tous = (groupes: Set<string>) =>
    new Set(tour.scenes.filter((scene) => groupes.has(scene.groupId)).map((scene) => scene.id));
  const membres = (groupes: Set<string>) =>
    tour.scenes
      .filter((scene) => groupes.has(scene.groupId) && !masquees.has(scene.id))
      .sort((a, b) => ordreDeLecture(tour, a, b));

  // Le domaine : ses scènes propres, pas celles de ses espaces.
  const zoneRacine = zoneDe(racine);
  espaces.push({
    id: racine.id,
    titre: racine.title,
    ...(zoneRacine ? { zone: zoneRacine } : null),
    niveaux: planRacine ? [{ groupeId: racine.id, image: planRacine, style: "illustration" }] : [],
    scenes: membres(new Set([racine.id])).map((scene) => ({
      scene,
      niveau: 0,
      ...(scene.mapPosition ? { position: scene.mapPosition } : null),
    })),
    membres: tous(new Set([racine.id])),
    sousZones: [],
  });

  for (const groupe of childGroups(tour, racine.id)) {
    const reglage = parEspace[groupe.id] ?? {};
    const groupes = sousArbre(tour, groupe.id);

    // Niveaux : les sous-groupes qui portent un plan (RDC, étage) ; à défaut le
    // plan de l'espace ; à défaut l'illustration du domaine, recadrée.
    const sousPlans = [...groupes]
      .map((id) => tour.groups.find((g) => g.id === id)!)
      .filter((g) => g.id !== groupe.id && g.map)
      .sort((a, b) => (a.level ?? a.order) - (b.level ?? b.order));
    const style: StylePlan = reglage.style ?? "architecte";
    const niveaux: Niveau[] =
      sousPlans.length > 0
        ? sousPlans.map((g) => ({ groupeId: g.id, libelle: g.title, image: g.map!.image, style }))
        : groupe.map
          ? [{ groupeId: groupe.id, image: groupe.map.image, style }]
          : planRacine
            ? [
                {
                  groupeId: racine.id,
                  image: planRacine,
                  style: reglage.style ?? "illustration",
                  ...(enRecadrage(reglage.recadrage) ? { recadrage: enRecadrage(reglage.recadrage)! } : null),
                },
              ]
            : [];

    const scenes = membres(groupes).map((scene) => {
      // Le niveau dont le plan s'applique à la scène ; sinon le premier.
      const proprietaire = mapOwnerGroup(tour, scene.groupId)?.id;
      const rang = Math.max(0, niveaux.findIndex((n) => n.groupeId === proprietaire));
      const surCePlan = niveaux[rang]?.groupeId === proprietaire;
      // Un sous-groupe plus profond se range sous la sous-zone qui le contient.
      const chemin = groupPath(tour, scene.groupId);
      const sousZone = chemin[chemin.findIndex((g) => g.id === groupe.id) + 1]?.id;
      return {
        scene,
        niveau: rang,
        ...(scene.mapPosition && surCePlan ? { position: scene.mapPosition } : null),
        ...(sousZone ? { sousZone } : null),
      };
    });
    const sousZones = childGroups(tour, groupe.id)
      .filter((g) => scenes.some((entree) => entree.sousZone === g.id))
      .map((g) => ({ id: g.id, titre: g.title }));

    const zone = zoneDe(groupe);
    espaces.push({
      id: groupe.id,
      titre: groupe.title,
      ...(zone ? { zone } : null),
      ...(reglage.entree && scenes.some((e) => e.scene.id === reglage.entree) ? { entree: reglage.entree } : null),
      niveaux,
      scenes,
      membres: tous(groupes),
      sousZones,
    });
  }
  return espaces.filter((espace) => espace.scenes.length > 0);
}

/** L'espace d'une scène. */
export function espaceDe(espaces: readonly Espace[], sceneId: string): Espace | undefined {
  return espaces.find((espace) => espace.membres.has(sceneId));
}
